import { useEffect, useState } from 'react';
import { paperWidth, receiptCopies, setPaperWidth, setReceiptCopies } from '../print';
import { oskEnabled, useKeyboard } from './useKeyboard';
import { openCashDrawer } from '../print';
import { MoneyPrompt, TextPrompt } from './Prompts';
import { escposEncode } from '@dastarhan/core';
import { getAgentUrl, pingAgent, printViaAgent, setAgentUrl } from '../print';
import {
  desktopGetSettings, desktopPrinters, desktopSaveSettings,
  isDesktopApp, type DeviceSettings,
} from '../print';
import { BASE } from '../api';

const LOCATION_LS = 'dastarhan.location';

/**
 * «⚙ Ещё»: операции смены и настройка устройства — в двух вкладках.
 *
 * Раньше это было одно окно «Агент термопринтера», куда со временем
 * съехалось всё: стоп-лист, X-отчёт, замок, наличные, языки, уклады.
 * Частые ОПЕРАЦИИ смены тонули среди настроек, которые трогают раз
 * при установке. Теперь операции — первая вкладка и первый взгляд,
 * настройка устройства — вторая, для того, кто кассу настраивает.
 */
export function PrinterDialog({ waiter, onWaiter, afterOrder, onAfterOrder, onCashOp, onStopList, onXReport, onLock, onClose , canDrawer = false }: {
  waiter: boolean;
  onWaiter: (v: boolean) => void;
  /** куда попадаем после оформления: список заказов или чистый экран */
  afterOrder: 'list' | 'desk';
  onAfterOrder: (v: 'list' | 'desk') => void;
  /** внесение/изъятие наличных: депозит брони, размен, инкассация */
  onCashOp: (kind: 'PAY_IN' | 'PAY_OUT', amount: number, comment: string) => void;
  /** право открывать ящик без продажи */
  canDrawer?: boolean;
  onStopList: () => void;
  /** X-отчёт: промежуточный итог смены печатью, без закрытия */
  onXReport: () => void;
  /** запереть экран: обратно только по PIN; офлайн — недоступно */
  onLock: (() => void) | null;
  onClose: () => void;
}) {
  const [osk, setOsk] = useState(() => oskEnabled());
  // Деньги в ящике вводим своей клавиатурой: системный prompt
  // на планшете либо заблокирован, либо не даёт цифр
  const [cashOp, setCashOp] = useState<'PAY_IN' | 'PAY_OUT' | null>(null);
  const [cashAmount, setCashAmount] = useState(0);
  const [tab, setTab] = useState<'ops' | 'setup'>('ops');

  /*
   * Программа для Windows печатает сама: вместо адресов http-агентов —
   * выбор принтеров из системы. Имена у каждой машины свои, угадывать
   * их человеку не следует, поэтому список, а не поле ввода.
   */
  const dsk = isDesktopApp();
  const [prns, setPrns] = useState<string[]>([]);
  const [dev, setDev] = useState<DeviceSettings>({});
  const loadPrinters = () => { void desktopPrinters().then(setPrns); };
  useEffect(() => {
    if (!dsk) return;
    loadPrinters();
    void desktopGetSettings().then(setDev);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dsk]);
  const pickPrinter = (key: 'printerName' | 'kitchenPrinter' | 'barPrinter', name: string) => {
    setDev((d) => ({ ...d, [key]: name }));
    void desktopSaveSettings({ [key]: name });
  };
  // Узкие принтеры 58 мм дают 24 символа — чек должен знать ширину
  const [kUrl, setKUrl] = useState(localStorage.getItem('dastarhan.printer.kitchen') ?? '');
  const [bUrl, setBUrl] = useState(localStorage.getItem('dastarhan.printer.bar') ?? '');
  const [uiLang, setUiLang] = useState<'ru' | 'kk'>(
    (localStorage.getItem('dastarhan.posLang') as 'ru' | 'kk') ?? 'ru');
  const [lang, setLang] = useState<'ru' | 'kk'>(
    (localStorage.getItem('dastarhan.receiptLang') as 'ru' | 'kk') ?? 'ru');
  const [url, setUrl] = useState(getAgentUrl() ?? 'http://127.0.0.1:9101');
  // Лента и копии: XP-80C и большинство стационарных — 80 мм,
  // это 48 символов. Узкие мобильные дают 32
  const [paper, setPaper] = useState<32 | 48>(() => paperWidth());
  const [copies, setCopies] = useState(() => receiptCopies());
  // Адреса принтеров вводят с того же планшета, где нет клавиатуры.
  // Настройка редкая, но без неё касса не печатает вовсе
  const kbUrl = useKeyboard(url, setUrl, 'text');
  const [status, setStatus] = useState<'idle' | 'busy' | 'ok' | 'fail'>('idle');

  const test = async () => {
    setStatus('busy');
    if (!dsk) setAgentUrl(url.trim());
    const ok = await printViaAgent(escposEncode([
      'Dastarhan', 'Проверка печати', '────────', 'Если вы это читаете —', 'принтер подключён верно',
    ]));
    setStatus(ok ? 'ok' : 'fail');
  };

  return (
    <div className="dialog-backdrop" role="dialog" aria-modal="true" aria-label="Смена и настройки">
      <div className="dialog" style={{ maxWidth: 460, width: '100%' }}>
        <span className="eyebrow">Касса</span>
        <h1>Смена и настройки</h1>
        <div className="row" role="tablist" aria-label="Разделы">
          <button className={`chip ${tab === 'ops' ? 'on' : ''}`} role="tab"
            aria-selected={tab === 'ops'} onClick={() => setTab('ops')}>
            Операции смены
          </button>
          <button className={`chip ${tab === 'setup' ? 'on' : ''}`} role="tab"
            aria-selected={tab === 'setup'} onClick={() => setTab('setup')}>
            Устройство и печать
          </button>
        </div>

        {tab === 'ops' && (
          <>
            <button className="btn" style={{ width: '100%' }} onClick={() => { onStopList(); onClose(); }}>
              🚫 Стоп-лист точки
            </button>
            <button className="btn" style={{ width: '100%' }} onClick={onXReport}
              title="Итог смены сейчас — без закрытия и без обнуления">
              📄 X-отчёт (итог без закрытия)
            </button>
            <button className="btn" style={{ width: '100%' }} disabled={!onLock}
              title={onLock ? 'Экран запрётся — вход по PIN' : 'Нужна связь: PIN проверяет сервер'}
              onClick={() => onLock?.()}>
              🔒 Запереть экран
            </button>
            <div className="row" style={{ width: '100%' }}>
              {(['PAY_IN', 'PAY_OUT'] as const).map((k) => (
                <button key={k} className="btn" style={{ flex: 1 }}
                  onClick={() => setCashOp(k)}>
                  {k === 'PAY_IN' ? '+ Внесение' : '− Изъятие'}
                </button>
              ))}
              {/* Ящик открывается принтером: он даёт импульс на замок.
                  Без этой кнопки размен берут ключом, и операция
                  нигде не отражается */}
              {canDrawer && (
                <button className="btn" style={{ flex: 1 }} onClick={() => {
                  void openCashDrawer().then((ok) => {
                    if (!ok) alert('Ящик открывает принтер — нужен агент печати');
                  });
                }}>
                  Открыть ящик
                </button>
              )}
            </div>
            <p className="hint" style={{ margin: 0 }}>
              Наличные мимо чека — депозит брони, размен, инкассация. Ожидаемая
              касса при закрытии смены учтёт эти строки.
            </p>
            <p className="hint" style={{ margin: 0 }}>
              ⌨ Горячие клавиши (<b>Ctrl+/</b> — шпаргалка целиком):{' '}
              <b>Ctrl+Z</b> — вернуть · <b>Ctrl+P</b> — счёт ·{' '}
              <b>Ctrl+Enter</b> — к оплате · <b>Esc</b> — закрыть окно.
            </p>
          </>
        )}

        {tab === 'setup' && (
          <>
            {!dsk && (
              <p className="hint" style={{ margin: 0 }}>
                Без агента чек печатается через браузер. Агент — маленькая программа
                на кассовом компьютере, она шлёт байты прямо в принтер и умеет QR.
              </p>
            )}
            {dsk && (
              <>
                <p className="hint" style={{ margin: 0 }}>
                  Печать идёт из самой программы, по очереди — агент не нужен.
                  Принтер (XPrinter XP-80 и подобные, USB) достаточно установить
                  в Windows; общий доступ открывать не требуется.
                </p>
                {([
                  ['printerName', 'Принтер чеков (касса)', '— принтер Windows по умолчанию —'],
                  ['kitchenPrinter', 'Принтер кухни (бегунки)', '— тот же, что чеки —'],
                  ['barPrinter', 'Принтер бара (бегунки)', '— тот же, что чеки —'],
                ] as const).map(([key, label, none]) => (
                  <label key={key} className="hint" style={{ display: 'block', margin: 0 }}>{label}
                    <select className="field" style={{ width: '100%' }}
                      value={dev[key] ?? ''}
                      onChange={(e) => pickPrinter(key, e.target.value)}>
                      <option value="">{none}</option>
                      {prns.map((p) => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </label>
                ))}
                <button className="btn small" onClick={loadPrinters}>
                  Обновить список принтеров
                </button>
              </>
            )}
            {/* Ширина ленты: ошибка не ломает печать, но делает её
                некрасивой — на 80-миллиметровой бумаге узкий чек
                занимает две трети листа, цена уезжает к середине */}
            <label className="prn-row">
              <span>Ширина ленты</span>
              <div className="prn-choices">
                {([48, 32] as const).map((w) => (
                  <button key={w} className={`btn ${paper === w ? 'primary' : ''}`}
                    onClick={() => { setPaper(w); setPaperWidth(w); }}>
                    {w === 48 ? '80 мм' : '58 мм'}
                  </button>
                ))}
              </div>
            </label>

            {/* Копии: одна гостю, вторая заведению — на ней кассир
                ставит подпись и кладёт в ящик */}
            <label className="prn-row">
              <span>Копий чека</span>
              <div className="prn-choices">
                {[1, 2, 3].map((n) => (
                  <button key={n} className={`btn ${copies === n ? 'primary' : ''}`}
                    onClick={() => { setCopies(n); setReceiptCopies(n); }}>
                    {n}
                  </button>
                ))}
              </div>
            </label>

            {!dsk && (
              <>
                <input {...kbUrl.props} className="field" style={{ width: '100%' }}
                  placeholder="http://127.0.0.1:9101"
                  value={url} onChange={(e) => setUrl(e.target.value)} />
                {kbUrl.node}
              </>
            )}
            {status === 'ok' && <p className="hint" style={{ color: 'var(--ok)', margin: 0 }}>Ушло на печать — проверьте ленту.</p>}
            {status === 'fail' && (
              <p className="error">
                {dsk
                  ? 'Принтер не ответил. Установлен ли он в Windows, включён ли?'
                  : 'Агент не ответил. Запущен ли он, задан ли принтер?'}
              </p>
            )}
            {!dsk && (
              <div className="hint" style={{ textAlign: 'left', margin: 0 }}>
                <AgentStatus />
              </div>
            )}
            {!dsk && (
              <>
                <label className="hint" style={{ display: 'block', margin: 0 }}>Принтер кухни (бегунки)
                  <input className="field" style={{ width: '100%' }} placeholder="http://127.0.0.1:9102"
                    value={kUrl} onChange={(e) => { setKUrl(e.target.value); localStorage.setItem('dastarhan.printer.kitchen', e.target.value.trim()); }} />
                </label>
                <label className="hint" style={{ display: 'block', margin: 0 }}>Принтер бара (бегунки)
                  <input className="field" style={{ width: '100%' }} placeholder="http://127.0.0.1:9103"
                    value={bUrl} onChange={(e) => { setBUrl(e.target.value); localStorage.setItem('dastarhan.printer.bar', e.target.value.trim()); }} />
                </label>
              </>
            )}
            <div className="row" style={{ alignItems: 'center' }}>
              <span className="hint" style={{ margin: 0 }}>Язык кассы:</span>
              {(['ru', 'kk'] as const).map((lg) => (
                <button key={'ui' + lg} className={`btn small ${uiLang === lg ? 'accent' : ''}`}
                  onClick={() => { setUiLang(lg); localStorage.setItem('dastarhan.posLang', lg); window.location.reload(); }}>
                  {lg === 'ru' ? 'Рус' : 'Қаз'}
                </button>
              ))}
              <span className="hint" style={{ margin: 0, marginLeft: 12 }}>Язык чека:</span>
              {(['ru', 'kk'] as const).map((lg) => (
                <button key={lg} className={`btn small ${lang === lg ? 'accent' : ''}`}
                  onClick={() => { setLang(lg); localStorage.setItem('dastarhan.receiptLang', lg); }}>
                  {lg === 'ru' ? 'Рус' : 'Қаз'}
                </button>
              ))}
            </div>
            <label className="waiter-toggle">
              <input type="checkbox" checked={waiter} onChange={(e) => onWaiter(e.target.checked)} />
              <span>
                <b>Режим официанта</b><br />
                <small>Без денег: «Отправить на кассу» вместо оплаты, смена без наличных.</small>
              </span>
            </label>

            {/* На Windows-планшете системная клавиатура не всплывает
                при касании поля, а на кассовом терминале физической
                может не быть вовсе */}
            <label className="waiter-toggle">
              <input type="checkbox" checked={osk}
                onChange={(e) => {
                  setOsk(e.target.checked);
                  // Пишем «0», а не стираем: в программе для Windows
                  // пустая настройка означает «включена из коробки»
                  localStorage.setItem('dastarhan.osk', e.target.checked ? '1' : '0');
                }} />
              <span>
                <b>Экранная клавиатура</b><br />
                <small>Своя клавиатура для полей ввода — в программе включена сразу.</small>
              </span>
            </label>

            {/* Куда попадает касса после оформления заказа.
                Два уклада работы, и оба живые:
                в зале официант ведёт несколько столов и хочет видеть список,
                за стойкой кассир бьёт чек за чеком и хочет чистый экран */}
            <div className="after-order">
              <b>После оформления заказа</b>
              <label className="waiter-toggle">
                <input type="radio" name="afterOrder" checked={afterOrder === 'list'}
                  onChange={() => onAfterOrder('list')} />
                <span>
                  <b>Показать список заказов</b><br />
                  <small>Для зала: видно все открытые столы и что на них.</small>
                </span>
              </label>
              <label className="waiter-toggle">
                <input type="radio" name="afterOrder" checked={afterOrder === 'desk'}
                  onChange={() => onAfterOrder('desk')} />
                <span>
                  <b>Остаться на рабочем столе</b><br />
                  <small>Для стойки: сразу чистый экран под следующий чек.</small>
                </span>
              </label>
            </div>
            <button className="btn" style={{ width: '100%' }}
              onClick={async () => {
                const loc = localStorage.getItem(LOCATION_LS);
                if (!loc) { alert('Точка неизвестна — переактивируйте кассу.'); return; }
                /*
                 * Адрес витрины: сохранённый при активации, иначе строим
                 * от корня сервера. Прежний способ приклеивал `menu.`
                 * к текущему хосту — в exe хоста нет вовсе (file://),
                 * а на одном домене по путям поддомена не существует.
                 */
                const stored = localStorage.getItem('dastarhan.menuUrl');
                const root = BASE.replace(/\/api\/v1$/, '') || window.location.origin;
                const menuUrl = stored || `${root}/menu/${loc}`;
                const ok = await printViaAgent(escposEncode([
                  'МЕНЮ И ЦЕНЫ', 'Наведите камеру на код', '',
                ], { qr: menuUrl }));
                alert(ok ? 'QR ушёл на печать — вырезайте и на стол.'
                         : dsk ? 'Принтер не ответил — выберите его выше.'
                              : 'Агент печати не ответил — настройте его выше.');
              }}>QR-меню зала на ленту</button>
          </>
        )}

        <div className="dialog-actions" style={{ width: '100%' }}>
          {tab === 'setup' && !dsk && (
            <button className="btn" onClick={() => { setAgentUrl(null); onClose(); }}>Печать браузером</button>
          )}
          <button className="btn ghost" onClick={onClose}>Закрыть</button>
          {tab === 'setup' && (
            <button className="btn accent" disabled={status === 'busy'} onClick={test}>
              {status === 'busy' ? 'Печатаем…' : 'Сохранить и проверить'}
            </button>
          )}
        </div>
      </div>


      {cashOp && cashAmount === 0 && (
        <MoneyPrompt
          title={cashOp === 'PAY_IN' ? 'Внесение в кассу' : 'Изъятие из кассы'}
          hint="Сумма наличными"
          onCancel={() => setCashOp(null)}
          onDone={(amount) => setCashAmount(amount)} />
      )}
      {cashOp && cashAmount > 0 && (
        <TextPrompt
          title="Зачем"
          hint="Депозит брони, размен, инкассация — эта строка попадёт в журнал"
          allowEmpty
          onCancel={() => { setCashOp(null); setCashAmount(0); }}
          onDone={(comment) => {
            onCashOp(cashOp, cashAmount, comment);
            setCashOp(null); setCashAmount(0);
            onClose();
          }} />
      )}
    </div>
  );
}

/** «Агент печати: отвечает / молчит» — состояние, а не настройка. */
function AgentStatus() {
  const [state, setState] = useState<'idle' | 'busy' | 'ok' | 'fail'>('idle');
  return (
    <>
      🖨 Агент печати:{' '}
      {state === 'ok' ? <b>отвечает ✓</b>
        : state === 'fail' ? <b>молчит — проверьте программу на компьютере</b>
          : state === 'busy' ? 'спрашиваем…' : 'не проверяли'}
      {' '}
      <button className="btn small" disabled={state === 'busy'}
        onClick={() => {
          setState('busy');
          void pingAgent().then((ok) => setState(ok ? 'ok' : 'fail'));
        }}>Проверить</button>
    </>
  );
}